import type { MaintenanceCandidate, MaintenanceExpectedState, MaintenanceInventory } from '@course-authoring/shared-types';
import { fetchWithAuthRetry } from './apiUtils';
import type { ScormExportContext } from './accountAssignmentApi';
import { buildPublishDeployTargets } from './publishApi';
import type { deployCoursePackage, DeployAccountInput, DeployResponse } from './courseDeployApi';
import type { LessonDeploymentPreparation } from './publishDelivery';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
export class MaintenanceApiError extends Error {
  constructor(message: string, public status: number, public code?: string, public notStarted = false) { super(message); }
}
async function readResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { message?: string; code?: string };
    throw new MaintenanceApiError(body.message ?? `Maintenance request failed (${response.status}).`, response.status, body.code,
      body.code === 'DEPLOY_NOT_STARTED' || response.status === 413);
  }
  return response.json() as Promise<T>;
}
const itemUrl = (item: Pick<MaintenanceCandidate, 'kind' | 'id'>) => `${API_BASE_URL}/admin/republish/${item.kind}/${encodeURIComponent(item.id)}`;
export async function fetchMaintenanceInventory(page = 1, cutoff?: string, filterByAge = true): Promise<MaintenanceInventory> {
  const query = new URLSearchParams({ page: String(page), filterByAge: String(filterByAge) });
  if (cutoff) query.set('cutoff', cutoff);
  return readResponse(await fetchWithAuthRetry(`${API_BASE_URL}/admin/republish?${query}`));
}
export interface MaintenancePublication {
  publishedAt: string;
  expected: MaintenanceExpectedState;
  context: ScormExportContext;
}
export async function publishMaintenanceItem(item: MaintenanceCandidate): Promise<MaintenancePublication> {
  return readResponse(await fetchWithAuthRetry(itemUrl(item), {
    method: 'POST', body: JSON.stringify({ observedPublication: item.lastPublishedAt }),
  }));
}
export async function refreshMaintenanceItem(item: MaintenanceCandidate): Promise<MaintenanceCandidate> {
  const current = await readResponse<{ lastPublishedAt: string | null }>(await fetchWithAuthRetry(itemUrl(item)));
  return { ...item, lastPublishedAt: current.lastPublishedAt };
}

export function guardedMaintenanceTransport(parent: MaintenanceExpectedState, expected = parent): typeof deployCoursePackage {
  return async (packageId, packageBase64, accounts, contentPublishedAt, ledger): Promise<DeployResponse> => {
    const response = await fetchWithAuthRetry(`${itemUrl(parent)}/deliver`, {
      method: 'POST', body: JSON.stringify({ packageId, packageBase64, accounts, contentPublishedAt, ...ledger, expected,
        ...(expected !== parent ? { parentExpected: parent } : {}) }),
    });
    if (response.status === 409) {
      const body = await response.clone().json().catch(() => ({})) as { failureReporting?: string; message?: string; recorded?: boolean };
      if (body.failureReporting === 'server') {
        return { results: accounts.map(account => ({ accountSlug: account.accountSlug, success: false,
          failureReporting: 'server', error: `${body.message ?? 'Prepared package was refused.'}${body.recorded === false ? ' Failure recording was unavailable.' : ''}` })) };
      }
    }
    return readResponse(response);
  };
}

export async function prepareMaintenanceLesson(
  parent: MaintenanceExpectedState, lessonId: string, accounts: DeployAccountInput[],
): Promise<LessonDeploymentPreparation> {
  const response = await readResponse<{ expected: MaintenanceExpectedState; context: ScormExportContext }>(await fetchWithAuthRetry(`${itemUrl(parent)}/prepare-lesson`, {
    method: 'POST', body: JSON.stringify({ expected: parent, lessonId, accountIds: accounts.map(account => account.externalAccountId ?? null) }),
  }));
  const selectedIds = new Set(accounts.map(account => account.externalAccountId ?? null));
  const targets = buildPublishDeployTargets(response.context).filter(account => selectedIds.has(account.externalAccountId ?? null));
  const revision = Math.max(...response.expected.states.map(state => state.lastPublishedAt ? Date.parse(state.lastPublishedAt) : -Infinity));
  return { accounts: targets, deployPackage: guardedMaintenanceTransport(parent, response.expected),
    contentPublishedAt: Number.isFinite(revision) ? new Date(revision).toISOString() : undefined };
}
